/**
 * 🏠 HEMBESÖKS-WORKFLOW UTILITIES
 * 
 * Hjälpfunktioner för att hantera workflow-baserad hembesöksstruktur
 */

/**
 * Hämtar hembesöksförslag från procurement-data
 * @param {Object} procurementData - Procurement-objektet
 * @returns {Object|null} - Hembesöksförslag eller null
 */
export const getHomeVisitProposals = (procurementData) => {
  if (!procurementData?.homeVisitWorkflow) return null;

  const workflow = procurementData.homeVisitWorkflow;
  
  // Returnera förslag i förväntat format
  return {
    proposals: workflow.proposalPhase?.proposals || [],
    message: workflow.proposalPhase?.message || '',
    sentAt: workflow.proposalPhase?.sentAt,
    sentBy: workflow.proposalPhase?.sentBy,
    emailSent: workflow.proposalPhase?.emailSent,
    customerResponse: {
      status: mapWorkflowStatusToOldFormat(workflow),
      acceptedProposal: workflow.customerResponse?.selectedProposal,
      acceptedAt: workflow.customerResponse?.respondedAt,
      message: workflow.customerResponse?.message,
      repliedAt: workflow.customerResponse?.respondedAt
    }
  };
};

/**
 * Hämtar bokad hembesökstid från procurement-data
 * @param {Object} procurementData - Procurement-objektet
 * @returns {Object|null} - Bokad tid eller null
 */
export const getBookedHomeVisit = (procurementData) => {
  if (!procurementData?.homeVisitWorkflow?.booking?.meetingAt) return null;

  const booking = procurementData.homeVisitWorkflow.booking;
  
  return {
    meetingAt: booking.meetingAt,
    calendarEventId: booking.calendarEventId,
    attendees: booking.attendees || [],
    bookedBy: booking.bookedBy,
    bookedAt: booking.bookedAt,
    notes: booking.notes || ''
  };
};

/**
 * Hämtar homeVisitSlot (används i HomeVisitProposalsSection)
 * Detta är ett alias för bokad tid
 */
export const getHomeVisitSlot = (procurementData) => {
  return getBookedHomeVisit(procurementData);
};

/**
 * Avgör om hembesöksförslag ska visas baserat på workflow-status
 * @param {Object} procurementData - Procurement-objektet
 * @returns {boolean} - true om förslag ska visas
 */
export const shouldShowHomeVisitProposals = (procurementData) => {
  if (!procurementData?.homeVisitWorkflow) return false;

  const workflow = procurementData.homeVisitWorkflow;
  
  // Visa endast om status är 'proposals_sent'
  // Dölj om kunden har svarat eller om hembesök är bokat
  return workflow.status === 'proposals_sent' && 
         workflow.proposalPhase?.proposals?.length > 0;
};

/**
 * Hämtar aktuell workflow-status i läsbart format
 * @param {Object} procurementData - Procurement-objektet
 * @returns {string} - Status i läsbart format
 */
export const getHomeVisitWorkflowStatus = (procurementData) => {
  return procurementData?.homeVisitWorkflow?.status || 'not_started';
};

/**
 * Hjälpfunktion: Mappar workflow-status till gammalt format för customerResponse.status
 * @private
 */
const mapWorkflowStatusToOldFormat = (workflow) => {
  if (!workflow.customerResponse?.type) return 'pending';
  
  switch (workflow.customerResponse.type) {
    case 'accepted':
      return 'accepted';
    case 'declined':
    case 'needs_new_times':
      return 'replied';
    default:
      return 'pending';
  }
};

/**
 * Debug-funktion: Loggar hembesöks-workflow state
 * @param {Object} procurementData - Procurement-objektet
 */
export const debugHomeVisitWorkflow = (procurementData) => {
  if (!procurementData) {
    return;
  }
};
